import React, { useEffect, useState } from 'react';
import { Cookie } from 'lucide-react';
import { analyticsConsent, setAnalyticsConsent } from '../utils/analytics';

// Full-width sheet that slides up from the bottom edge. "Allow cookies" is the clear
// primary action; "No thanks" stays visible so declining is always one tap away.
export const AnalyticsConsent = () => {
  const [open, setOpen] = useState(() => analyticsConsent() === 'unknown');
  const [animateIn, setAnimateIn] = useState(false);

  useEffect(() => {
    if (!open) { setAnimateIn(false); return undefined; }
    const timer = setTimeout(() => setAnimateIn(true), 400);
    return () => clearTimeout(timer);
  }, [open]);

  const choose = (value) => {
    setAnimateIn(false);
    setTimeout(() => { setAnalyticsConsent(value); setOpen(false); }, 300);
  };

  return open ? (
    <aside
      data-testid="analytics-consent"
      aria-label="Cookie preferences"
      role="dialog"
      className={`fixed z-[70] inset-x-0 bottom-0 border-t border-purple-200 bg-white shadow-[0_-16px_48px_-12px_rgba(76,29,149,0.35)] text-slate-800 transition-transform duration-300 ease-out ${animateIn ? 'translate-y-0' : 'translate-y-full'}`}
    >
      <div className="max-w-5xl mx-auto px-5 sm:px-8 pt-7 pb-[calc(1.75rem+env(safe-area-inset-bottom))] sm:py-10 flex flex-col md:flex-row md:items-center gap-5 md:gap-10">
        <div className="flex items-start gap-4 flex-1">
          <span aria-hidden="true" className="shrink-0 w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <Cookie className="w-5 h-5 text-white" />
          </span>
          <div>
            <p data-testid="analytics-consent-title" className="font-bold text-lg mb-1.5">We use cookies</p>
            <p data-testid="analytics-consent-description" className="text-sm leading-relaxed text-slate-600">
              We use necessary cookies to make our site work. We'd also like to set optional analytics and advertising cookies (Google, Meta and TikTok) to improve the site and measure our ads. We won't set optional cookies unless you enable them.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 md:shrink-0">
          <button
            data-testid="analytics-decline"
            onClick={() => choose(false)}
            className="rounded-xl px-5 py-3.5 text-sm font-semibold text-slate-500 hover:text-slate-800 hover:bg-slate-100 focus-visible:outline-purple-600"
          >
            No thanks
          </button>
          <button
            data-testid="analytics-allow"
            onClick={() => choose(true)}
            className="flex-1 md:flex-none rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 px-10 py-3.5 text-base text-white font-bold shadow-lg shadow-purple-600/25 hover:shadow-purple-600/40 hover:-translate-y-0.5 transition-all focus-visible:outline-purple-400"
          >
            Allow cookies
          </button>
        </div>
      </div>
    </aside>
  ) : (
    <button
      data-testid="analytics-settings"
      onClick={() => setOpen(true)}
      className="fixed z-40 bottom-2 left-2 rounded-lg border border-purple-200 bg-white/95 text-purple-900 px-2 py-1 text-[10px] shadow-sm hover:bg-purple-50"
    >
      Privacy choices
    </button>
  );
};
